import type { AxiosError } from 'axios';
import type { ErrorClassification } from './errorHandling.types';

/**
 * Single source of truth for "what kind of failure is this" across the app.
 * Every status-code decision (retry? severity?) is derived from this
 * classification instead of being re-decided ad hoc per call site.
 */
export function classifyAxiosError(error: AxiosError): ErrorClassification {
  const statusCode = error.response?.status ?? null;

  if (statusCode === null) {
    // No response received at all — offline, DNS failure, CORS, timeout, server unreachable.
    return {
      category: 'network',
      isRetryable: true,
      severity: 'error',
      statusCode,
    };
  }

  if (statusCode === 401) {
    return {
      category: 'auth',
      isRetryable: false,
      severity: 'info',
      statusCode,
    };
  }

  if (statusCode === 408 || statusCode === 429) {
    // Request timeout / rate limited — plausibly transient, safe to retry.
    return {
      category: 'client',
      isRetryable: true,
      severity: 'warning',
      statusCode,
    };
  }

  if (statusCode >= 400 && statusCode < 500) {
    // Bad input, forbidden, not found, etc. — retrying won't change the outcome.
    return {
      category: 'client',
      isRetryable: false,
      severity: 'warning',
      statusCode,
    };
  }

  if (statusCode >= 500) {
    return {
      category: 'server',
      isRetryable: true,
      severity: 'critical',
      statusCode,
    };
  }

  return {
    category: 'client',
    isRetryable: false,
    severity: 'error',
    statusCode,
  };
}

/**
 * A network-category AxiosError has no response to inspect, so axios's generic
 * "Network Error" message tells you nothing. This turns the browser/axios signals
 * that *are* available (online status, axios's own failure code) into an actual reason.
 */
export function describeNetworkError(error: AxiosError): string {
  const isOffline =
    typeof navigator !== 'undefined' && navigator.onLine === false;

  if (isOffline) {
    return 'Network error — the browser reports no internet connection (offline).';
  }

  switch (error.code) {
    case 'ECONNABORTED':
    case 'ETIMEDOUT': {
      return `Network error — request timed out (${error.message}).`;
    }
    case 'ERR_CANCELED': {
      return 'Network error — the request was canceled before it completed.';
    }
    case 'ERR_NETWORK': {
      return 'Network error — no response received. Likely causes: CORS block, DNS failure, connection refused, or the server is unreachable.';
    }
    default: {
      const codeSuffix = error.code ? ` (code: ${error.code})` : '';
      return `Network error — ${error.message || 'no response received'}${codeSuffix}.`;
    }
  }
}
